import { Container, Rectangle, SCALE_MODES, Sprite, Texture } from "pixi.js"

export interface RockRippleContact { id: string; x: number; line: number; lift: number; radius: number; z: number; flowX: number; flowY: number }
interface Ripple { wake: Sprite; scale: number; phase: number; x: number; y: number; dx: number; dy: number }

/** Small broken pressure ripples and trailing flecks; stationary rocks do not emit concentric rings. */
export class WaterRockRipples {
    private atlas?: Texture
    private textures?: Texture[]
    private ripples = new Map<string, Ripple>()

    private prepare = (): Texture[] => {
        if (this.textures) return this.textures
        const rgba = new Uint8Array(64 * 32 * 4)
        for (let y = 0; y < 32; y++) for (let x = 0; x < 64; x++) {
            if ((x - 32) ** 2 + (y - 16) ** 2 < 60 && (x * 11 + y * 7) % 29 < 2) {
                rgba.set([140, 198, 200, 255], (y * 64 + x) * 4)
            }
        }
        this.atlas = Texture.fromBuffer(rgba, 64, 32, { scaleMode: SCALE_MODES.NEAREST })
        this.textures = [0].map(i => new Texture(this.atlas!.baseTexture, new Rectangle(i * 64, 0, 64, 32)))
        return this.textures
    }

    sync = (contacts: RockRippleContact[], surface: Container): void => {
        const wanted = new Set<string>()
        for (const contact of contacts) {
            wanted.add(contact.id)
            let ripple = this.ripples.get(contact.id)
            if (!ripple) {
                const [wake] = this.prepare().map(texture => new Sprite(texture))
                for (const sprite of [wake]) { sprite.anchor.set(.5); sprite.name = "rock-water-ripple"; surface.addChild(sprite) }
                const phase = [...contact.id].reduce((h, c) => (Math.imul(h, 31) + c.charCodeAt(0)) | 0, 7)
                ripple = { wake, scale: 1, phase: (phase >>> 0) % 1000 / 1000, x: 0, y: 0, dx: 0, dy: 1 }
                this.ripples.set(contact.id, ripple)
            }
            ripple.scale = contact.radius / 26
            ripple.x = contact.x; ripple.y = contact.line - contact.lift
            ripple.dx = contact.flowX; ripple.dy = contact.flowY
            for (const sprite of [ripple.wake]) {
                if (sprite.parent !== surface) surface.addChild(sprite)
                sprite.visible = true; sprite.scale.set(ripple.scale)
                sprite.position.set(ripple.x, ripple.y)
            }
            ripple.wake.zIndex = contact.z - .02
        }
        this.ripples.forEach((ripple, id) => {
            if (wanted.has(id)) return
            ripple.wake.destroy(); this.ripples.delete(id)
        })
    }

    tick = (seconds: number): void => this.ripples.forEach(ripple => {
        const phase = (seconds / 3.8 + ripple.phase) % 1
        ripple.wake.position.set(ripple.x + ripple.dx * (4 + phase * 9), ripple.y + ripple.dy * (4 + phase * 9))
        ripple.wake.alpha = (1 - phase) ** 2 * .25
    })

    displayObjects = (): Sprite[] => [...this.ripples.values()].flatMap(r => [r.wake])
    clear = (): void => { this.displayObjects().forEach(sprite => sprite.destroy()); this.ripples.clear() }
    destroy = (): void => { this.clear(); this.textures?.forEach(texture => texture.destroy()); this.atlas?.destroy(true) }
}
